import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Ion } from "@/components/Ion";
import { OCard, OrigHeader, PrimaryBtn, Screen, Spinner, useBack } from "@/components/Orig";
import { useTheme } from "@/lib/theme";

const Step = ({ n, status, title, body, children }) => {
  const theme = useTheme();
  const done = status === "done";
  return (
    <OCard className="mb-4" style={{ borderColor: done ? theme.accent : theme.border, opacity: status === "locked" ? 0.55 : 1 }} data-testid={`training-step-${n}`}>
      <div className="mb-4 flex gap-4">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[13px] font-extrabold" style={{ background: done ? theme.accent : theme.cardElevated, borderColor: theme.border }}>
          {done ? <Ion name="checkmark" size={16} color="#000" /> : n}
        </span>
        <div className="flex-1"><p className="text-base font-bold">{title}</p><p className="mt-1 text-[13px] leading-5" style={{ color: theme.textSecondary }}>{body}</p></div>
      </div>
      {children}
    </OCard>
  );
};

const Pill = ({ icon, text, color, bg }) => (
  <span className="flex items-center gap-1.5 self-start rounded-full border px-3 py-2 text-[11px] font-extrabold tracking-[2px]" style={{ color, borderColor: color, background: bg }}><Ion name={icon} size={14} color={color} />{text}</span>
);

export function Training() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [course, setCourse] = useState(null);
  const [completion, setCompletion] = useState({ completed: false });
  const [cert, setCert] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([api("/training/course"), api("/training/completion/me"), api("/certificates/me")])
      .then(([c, comp, certs]) => { setCourse(c); setCompletion(comp); setCert(certs.length > 0); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      setCompletion(await api("/training/completion", { method: "POST", body: { code: code.trim() } }));
      setCode("");
      window.alert("Course marked complete. Corporate will issue your LSO training completion certificate after review.");
    } catch (e) { setErr(e.message || "Invalid code"); } finally { setBusy(false); }
  };
  const st = (i) => i === 1 ? (completion.completed ? "done" : "active")
    : i === 2 ? (completion.completed ? (cert ? "done" : "active") : "locked")
    : cert ? "done" : completion.completed ? "active" : "locked";

  return (
    <Screen testId="training-page">
      <OrigHeader eyebrow="LSO TRAINING" title={course?.title || "Laser Safety Officer Training"} titleSize={20} />
      {loading ? <Spinner /> : (
        <div className="px-6 pb-8">
          <p className="mb-6 text-[13px] leading-5" style={{ color: theme.textSecondary }}>{course?.description}</p>
          <Step n={1} status={st(1)} title="Complete the Online Course" body="Work through every module of the ALR LSO course. The course runs entirely inside the app — auth-gated.">
            <PrimaryBtn data-testid="start-course" onClick={() => navigate("/training-course")} disabled={!course?.course_url}><Ion name="play" size={16} color="#000" />{completion.completed ? "REVIEW COURSE" : "START COURSE"}</PrimaryBtn>
          </Step>
          <Step n={2} status={st(2)} title="Enter ALR Completion Code"
            body={completion.completed ? `Verified${completion.completed_at ? ` on ${new Date(completion.completed_at).toLocaleDateString()}` : ""}.` : "After you complete the online course, ALR Corporate will give you a completion code. Enter it here to mark the course complete."}>
            {!completion.completed && <>
              <input data-testid="completion-code" placeholder="Enter code from ALR" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(null); }}
                className="mb-2 w-full rounded-lg border border-[#333] bg-[#121212] px-4 py-3.5 text-center text-base font-bold tracking-[2px] text-white placeholder:text-[#A0A0A0] focus:outline-none" />
              {err && <p className="mb-2 text-center text-xs text-[#FF3333]" data-testid="completion-code-error">{err}</p>}
              <PrimaryBtn data-testid="submit-code" onClick={submit} disabled={!code.trim() || busy}>{busy ? "VERIFYING…" : "SUBMIT CODE"}</PrimaryBtn>
            </>}
          </Step>
          <Step n={3} status={st(3)} title="Training Completion Certificate (Issued by Corporate)"
            body={cert ? "Certificate on file — issued by ALR Corporate." : completion.completed ? "Awaiting corporate review. ALR Corporate will upload your signed certificate here after verifying your completion." : "Once you complete the course, ALR Corporate will review and upload your signed certificate here."}>
            {cert ? <Pill icon="shield-checkmark" text="TRAINING COMPLETE" color={theme.accent} bg="rgba(76, 204, 6,0.1)" /> : completion.completed ? <Pill icon="time-outline" text="PENDING CORPORATE" color={theme.warning} bg="rgba(255,184,0,0.1)" /> : null}
          </Step>
          {completion.completed && cert && (
            <div className="mt-4 flex flex-col items-center rounded-[14px] border p-6" style={{ background: theme.cardElevated, borderColor: theme.accent }} data-testid="training-complete">
              <Ion name="trophy" size={40} color={theme.accent} />
              <p className="mt-4 text-[13px] font-extrabold tracking-[3px]" style={{ color: theme.accent }}>LSO TRAINING COMPLETE</p>
              <p className="mt-2 text-center text-[13px] leading-5" style={{ color: theme.textSecondary }}>You've finished the course and ALR Corporate has issued your certificate.</p>
            </div>
          )}
        </div>
      )}
    </Screen>
  );
}

export function TrainingCourse() {
  const theme = useTheme();
  const back = useBack();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api("/training/course").then(setCourse).catch(() => {}).finally(() => setLoading(false)); }, []);
  const url = course?.course_url;
  return (
    <Screen testId="training-course-page" className="flex h-screen flex-col">
      <OrigHeader compact eyebrow="LSO COURSE" title={course?.title || "LSO Training"} titleSize={16}
        right={url && <button data-testid="open-external" onClick={() => window.open(url, "_blank")} className="flex h-10 w-10 items-center justify-center rounded-[10px] border" style={{ background: theme.card, borderColor: theme.border }}><Ion name="open-outline" size={20} color={theme.accent} /></button>} />
      <div className="flex flex-1 flex-col bg-black">
        {loading ? <Spinner /> : !url ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6" data-testid="course-empty">
            <Ion name="school-outline" size={48} color={theme.textSecondary} /><p className="text-sm font-semibold">No course configured yet.</p>
            <p className="text-center text-xs" style={{ color: theme.textSecondary }}>Admin can set the course URL in the Admin Console.</p>
          </div>
        ) : <iframe title="LSO Training Course" src={url} allow="autoplay; fullscreen; clipboard-read; clipboard-write" allowFullScreen className="w-full flex-1 border-0" />}
      </div>
      {url && (
        <div className="flex flex-col gap-2 border-t p-4" style={{ borderColor: theme.border, background: theme.bg }}>
          <p className="text-center text-xs" style={{ color: theme.textSecondary }}>When you finish, tap "Back" and mark the course complete.</p>
          <PrimaryBtn data-testid="course-back-complete" onClick={back} className="py-3 text-xs tracking-[2px]">BACK TO TRAINING</PrimaryBtn>
        </div>
      )}
    </Screen>
  );
}

export function EmployeeOnboarding() {
  const navigate = useNavigate();
  const theme = useTheme();
  const back = useBack("/dashboard");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api("/employee-onboarding/me").then((d) => { setData(d); setErr(null); }).catch((e) => setErr(e.message || "Couldn't load your checklist.")); }, []);
  const ack = async (key) => {
    setBusy(key);
    try { setData(await api(`/employee-onboarding/me/ack/${key}`, { method: "POST" })); } catch (e) { setErr(e.message || "Couldn't save. Try again."); } finally { setBusy(null); }
  };
  return (
    <Screen testId="employee-onboarding-page">
      <div className="flex items-center border-b px-2 py-2" style={{ borderColor: theme.border }}>
        <button onClick={back} className="flex h-11 w-11 items-center justify-center" data-testid="onboarding-back"><Ion name="chevron-back" size={22} color={theme.text} /></button>
        <h1 className="text-lg font-extrabold">My Onboarding</h1>
      </div>
      <div className="p-4 pb-12">
        {!data && !err && <Spinner />}
        {err && <p className="mt-3 text-[#FF3333]">{err}</p>}
        {data && <>
          <OCard data-testid="onboarding-progress">
            <p className="text-xl font-extrabold">{data.completed} of {data.total} complete</p>
            <div className="my-2.5 h-2 overflow-hidden rounded" style={{ background: theme.border }}><div className="h-2" style={{ width: `${(data.completed / data.total) * 100}%`, background: theme.accent }} /></div>
            <p className="text-[13px]" style={{ color: theme.textSecondary }}>{data.complete ? "You're all set. Nice work." : "Finish each item below. Your employer can see your progress."}</p>
          </OCard>
          {data.items.map((i) => (
            <OCard key={i.key} className="mt-3" style={{ borderColor: i.done ? theme.accent : theme.border }} data-testid={`onboarding-item-${i.key}`}>
              <div className="flex items-center gap-2.5"><Ion name={i.done ? "checkmark-circle" : "ellipse-outline"} size={22} color={i.done ? theme.accent : theme.textSecondary} /><p className="flex-1 text-[15px] font-extrabold">{i.title}</p></div>
              <p className="mt-2 text-[13px] leading-[19px]" style={{ color: theme.textSecondary }}>{i.detail}</p>
              <div className="mt-3 flex gap-2">
                {i.route && <button onClick={() => navigate(i.route)} className="min-h-11 flex-1 rounded-lg border text-[13px] font-bold" style={{ borderColor: theme.border }}>{i.kind === "lso" ? "Open training" : "Open"}</button>}
                {i.kind === "ack" && !i.done && <button onClick={() => ack(i.key)} disabled={busy === i.key} data-testid={`onboarding-ack-${i.key}`} className="min-h-11 flex-1 rounded-lg text-[13px] font-extrabold text-black" style={{ background: theme.accent }}>{busy === i.key ? "…" : "I've read & agree"}</button>}
              </div>
            </OCard>
          ))}
          <OCard className="mt-3">
            <div className="flex items-center gap-2.5"><Ion name={data.unsupervised_authorized ? "checkmark-circle" : "hand-left-outline"} size={22} color={data.unsupervised_authorized ? theme.accent : "#F59E0B"} /><p className="flex-1 text-[15px] font-extrabold">Unsupervised laser operation</p></div>
            <p className="mt-2 text-[13px] leading-[19px]" style={{ color: theme.textSecondary }}>{data.unsupervised_authorized ? "Your employer has authorized you to operate laser equipment without supervision." : "Operate laser equipment only under supervision until your employer authorizes you. Your LSO certificate alone does not authorize unsupervised work."}</p>
          </OCard>
          <div className="mt-4 flex gap-2 rounded-[14px] border p-4" style={{ borderColor: theme.border }}><Ion name="document-text-outline" size={16} color={theme.textSecondary} /><p className="flex-1 text-xs leading-[18px]" style={{ color: theme.textSecondary }}>{data.paperwork_note}</p></div>
          <p className="mt-3 text-[11px] italic leading-4" style={{ color: theme.textSecondary }} data-testid="onboarding-disclaimer">{data.disclaimer}</p>
        </>}
      </div>
    </Screen>
  );
}

const LtHeader = ({ eyebrow, title, size = 24 }) => {
  const theme = useTheme();
  const back = useBack();
  return (
    <div className="flex items-center gap-2 p-4 pb-0">
      <button onClick={back} className="p-2" data-testid="back-btn"><Ion name="chevron-back" size={22} color={theme.text} /></button>
      <div className="flex-1"><p className="text-[10px] font-bold tracking-[1px]" style={{ color: theme.accent }}>{eyebrow}</p><h1 className="mt-0.5 font-bold" style={{ fontSize: size }} data-testid="page-title">{title}</h1></div>
    </div>
  );
};
const SectionLabel = ({ children }) => <p className="mb-2 mt-4 text-[11px] font-bold tracking-[1px] text-[#A0A0A0]">{children}</p>;

const useLaunch = () => {
  const [program, setProgram] = useState(null);
  const [progress, setProgress] = useState(null);
  useEffect(() => {
    Promise.all([api("/launch-training/program"), api("/launch-training/progress/me")])
      .then(([p, pr]) => { setProgram(p); setProgress(pr); }).catch((e) => window.alert(`Couldn't load Launch Training\n\n${e.message || "Try again."}`));
  }, []);
  return { program, progress, setProgress };
};

export function LaunchTraining() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { program, progress } = useLaunch();
  if (!program || !progress) return <Screen testId="launch-training-page"><Spinner /></Screen>;
  return (
    <Screen testId="launch-training-page">
      <LtHeader eyebrow="FRANCHISEE ONBOARDING" title="Launch Training" />
      <div className="p-6 pb-8">
        <OCard className="mb-4"><p className="text-base font-bold">{program.title}</p><p className="mt-1 text-[13px] leading-[18px]" style={{ color: theme.textSecondary }}>{program.subtitle}</p></OCard>
        <OCard className="mb-4" data-testid="launch-progress">
          <div className="flex items-center justify-between"><p className="text-base font-bold">Your Progress</p><p className="text-lg font-bold" style={{ color: theme.accent }}>{progress.percent}%</p></div>
          <div className="mt-2 h-2 overflow-hidden rounded-full" style={{ background: theme.cardElevated }}><div className="h-full" style={{ width: `${progress.percent}%`, background: theme.accent }} /></div>
          <p className="mt-2 text-xs" style={{ color: theme.textSecondary }}>{progress.completed_at ? "✨ Launch Training complete! Submit your Training Completion Record to HQ." : `${progress.done_items} of ${progress.total_items} checklist items complete`}</p>
        </OCard>
        <SectionLabel>5 DAYS</SectionLabel>
        {program.days.map((d) => {
          const dp = progress.by_day.find((p) => p.day === d.day);
          const pct = dp && dp.total_count > 0 ? Math.round((dp.completed_count / dp.total_count) * 100) : 0;
          return (
            <button key={d.day} onClick={() => navigate(`/launch-training/day/${d.day}`)} data-testid={`launch-day-${d.day}`} className="mb-2 flex w-full items-center gap-4 rounded-[14px] border p-4 text-left" style={{ background: theme.card, borderColor: dp?.is_complete ? theme.accent : theme.border }}>
              <span className="flex h-10 w-10 items-center justify-center rounded-full text-lg font-extrabold" style={{ background: dp?.is_complete ? theme.accent : theme.cardElevated, color: pct > 0 ? theme.accent : theme.text }}>{dp?.is_complete ? <Ion name="checkmark" size={18} color="#000" /> : d.day}</span>
              <span className="flex-1">
                <span className="block text-sm font-bold">Day {d.day} — {d.title}</span>
                <span className="mt-0.5 line-clamp-2 block text-xs" style={{ color: theme.textSecondary }}>{d.summary}</span>
                {dp && dp.total_count > 0 && <span className="mt-1.5 flex items-center gap-2"><span className="h-1 flex-1 overflow-hidden rounded-full" style={{ background: theme.cardElevated }}><span className="block h-full" style={{ width: `${pct}%`, background: theme.accent }} /></span><span className="text-[10px] font-semibold" style={{ color: theme.textSecondary }}>{dp.completed_count}/{dp.total_count}</span></span>}
              </span>
              <Ion name="chevron-forward" size={18} color={theme.textSecondary} />
            </button>
          );
        })}
        {program.post_launch?.length > 0 && <>
          <SectionLabel>AFTER LAUNCH</SectionLabel>
          {program.post_launch.map((pl, i) => <OCard key={i} className="mb-4"><p className="text-base font-bold">{pl.title}</p><p className="mt-2 text-[13px] leading-[19px]" style={{ color: theme.textSecondary }}>{pl.body}</p></OCard>)}
        </>}
      </div>
    </Screen>
  );
}

export function LaunchDay() {
  const { num } = useParams();
  const dayNum = parseInt(num || "1", 10);
  const navigate = useNavigate();
  const theme = useTheme();
  const { program, progress, setProgress } = useLaunch();
  const [busy, setBusy] = useState(false);
  const day = program?.days.find((d) => d.day === dayNum);
  if (program && progress && !day) return <Navigate to="/launch-training" replace />;
  if (!program || !progress) return <Screen testId="launch-day-page"><Spinner /></Screen>;
  const done = new Set(progress.by_day.find((p) => p.day === day.day)?.completed_indices || []);
  const next = program.days.find((d) => d.day === day.day + 1);
  const toggle = async (idx, cur) => {
    if (busy) return;
    setBusy(true);
    try { setProgress(await api("/launch-training/progress/me/toggle", { method: "POST", body: { day: day.day, item_index: idx, completed: !cur } })); }
    catch (e) { window.alert(e.message || "Try again."); } finally { setBusy(false); }
  };
  return (
    <Screen testId="launch-day-page">
      <LtHeader eyebrow={`DAY ${day.day} OF ${program.total_days}`} title={day.title} size={20} />
      <div className="p-6 pb-24">
        <p className="mb-4 text-sm leading-5">{day.summary}</p>
        {day.deliverables.length > 0 && (
          <div className="mb-4 flex gap-2 rounded-[14px] border p-4" style={{ background: theme.card, borderColor: theme.warning }}>
            <Ion name="alert-circle" size={18} color={theme.warning} />
            <div className="flex-1"><p className="text-[10px] font-bold tracking-[1px]" style={{ color: theme.warning }}>REQUIRED DELIVERABLE</p>{day.deliverables.map((d, i) => <p key={i} className="mt-1 text-[13px] font-semibold">• {d}</p>)}</div>
          </div>
        )}
        <SectionLabel>OPERATIONS MANUAL TOPICS</SectionLabel>
        <OCard className="mb-4">{day.manual_topics.map((t, i) => <div key={i} className="flex items-start gap-2 py-1.5"><span className="mt-[9px] h-[5px] w-[5px] shrink-0 rounded-full" style={{ background: theme.accent }} /><p className="flex-1 text-[13px] leading-5">{t}</p></div>)}</OCard>
        <SectionLabel>HOW TO USE THE APP TODAY</SectionLabel>
        {day.app_guidance.map((g, i) => (
          <OCard key={i} className="mb-2">
            <div className="flex items-center gap-2"><Ion name="sparkles" size={16} color={theme.accent} /><p className="text-sm font-bold" style={{ color: theme.accent }}>{g.feature}</p></div>
            <p className="mt-1.5 text-[13px] font-semibold leading-[18px]">{g.what}</p>
            <p className="mt-1.5 text-xs leading-[18px]" style={{ color: theme.textSecondary }}>{g.how}</p>
            {g.ai_prompt && <button onClick={() => navigate(`/ai-assistant?initial=${encodeURIComponent(g.ai_prompt)}`)} className="mt-2 flex w-full items-center gap-1.5 rounded-lg px-2 py-2 text-left" style={{ background: theme.accent }}><Ion name="chatbubble-ellipses" size={14} color="#000" /><span className="line-clamp-2 flex-1 text-xs font-semibold text-black">Ask the AI: “{g.ai_prompt}”</span></button>}
          </OCard>
        ))}
        <SectionLabel>TODAY'S CHECKLIST</SectionLabel>
        <OCard className="mb-4">
          {day.checklist.map((item, idx) => {
            const on = done.has(idx);
            return (
              <button key={idx} disabled={busy} onClick={() => toggle(idx, on)} data-testid={`launch-check-${idx}`} className="flex w-full items-center gap-2 py-2.5 text-left">
                <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border-2" style={on ? { background: theme.accent, borderColor: theme.accent } : { borderColor: theme.border }}>{on && <Ion name="checkmark" size={14} color="#000" />}</span>
                <span className={`flex-1 text-[13px] leading-[18px] ${on ? "line-through" : ""}`} style={{ color: on ? theme.textSecondary : theme.text }}>{item}</span>
              </button>
            );
          })}
        </OCard>
        {next && <PrimaryBtn onClick={() => navigate(`/launch-training/day/${next.day}`, { replace: true })} className="mt-6 px-4 font-bold tracking-normal" data-testid="launch-next-day">Continue to Day {next.day}: {next.title}<Ion name="arrow-forward" size={16} color="#000" /></PrimaryBtn>}
      </div>
    </Screen>
  );
}
